// Import the ESM build that bundles libphonenumber "utils" (the dependency
// behind number formatting and validation), so utils is attached up-front with
// no runtime fetch. We point at the .mjs entry directly because the package's
// `main` is a browser-global IIFE build (no module.exports) and webpack 4
// ignores the `exports` field that maps the bare specifier to the ESM entry.
import intlTelInput from 'intl-tel-input/dist/js/intlTelInputWithUtils.mjs'

import ar from 'intl-tel-input/dist/js/locale/ar'
import en from 'intl-tel-input/dist/js/locale/en'
import es from 'intl-tel-input/dist/js/locale/es'
import fr from 'intl-tel-input/dist/js/locale/fr'
import de from 'intl-tel-input/dist/js/locale/de'
import sv from 'intl-tel-input/dist/js/locale/sv'
import no from 'intl-tel-input/dist/js/locale/no'
import fi from 'intl-tel-input/dist/js/locale/fi'
import da from 'intl-tel-input/dist/js/locale/da'
import it from 'intl-tel-input/dist/js/locale/it'
import pt from 'intl-tel-input/dist/js/locale/pt'
import ru from 'intl-tel-input/dist/js/locale/ru'
import tr from 'intl-tel-input/dist/js/locale/tr'
import zh from 'intl-tel-input/dist/js/locale/zh'
import zhHk from 'intl-tel-input/dist/js/locale/zh-hk'

import i18n from '../i18n/i18n.js.erb'

const I18n = window.I18n || i18n

window.intlTelInput = intlTelInput
window.intlTelInputI18n = {
  ar: ar,
  en: en,
  es: es,
  fr: fr,
  de: de,
  sv: sv,
  nb: no,
  fi: fi,
  da: da,
  it: it,
  'pt-PT': pt,
  'pt-BR': pt,
  ru: ru,
  tr: tr,
  'zh-CN': zh,
  'zh-TW': zhHk,
}

const languageCountryMap = {
  ar: 'sa',
  sv: 'se',
  de: 'de',
  es: 'es',
  fr: 'fr',
  nb: 'no',
  da: 'dk',
  fi: 'fi',
  it: 'it',
  'pt-PT': 'pt',
  'pt-BR': 'br',
  ru: 'ru',
  tr: 'tr',
  'zh-CN': 'cn',
  'zh-TW': 'tw',
}

// Countries floated to the top of the dropdown: the event's own country first,
// then the countries behind the event's languages. Off an event page we offer
// the countries behind every language we support. Built per call rather than
// kept in module state, so one page's event cannot leak into the next one.
const buildCountryOrder = () => {
  if (!window.currentEvent) {
    return Object.values(languageCountryMap)
  }

  const countryOrder = [window.currentEvent.country.toLowerCase()]
  window.currentEvent.languages.forEach((lang) => {
    const country = languageCountryMap[lang]
    if (country && !countryOrder.includes(country)) {
      countryOrder.push(country)
    }
  })

  return countryOrder
}

// The country to preselect. countryOrder only sorts the dropdown, so its first
// entry is a poor default off an event page: it is whatever languageCountryMap
// happens to list first (Saudi Arabia), which then reads every number without
// an explicit dial code as a Saudi one. Prefer the event's country, then the
// signed-in user's, then the country behind the interface language, and
// otherwise preselect nothing and let intl-tel-input show its globe.
const initialCountry = () =>
  (
    window.currentEvent?.country ||
    window.currentUser?.country ||
    languageCountryMap[I18n.locale] ||
    ''
  ).toLowerCase()

window.initIntlTelInputs = (selector) => {
  const countryOrder = buildCountryOrder()

  document.querySelectorAll(selector).forEach((input) => {
    if (!window.intlTelInput.getInstance(input)) {
      // v29's hiddenInputs reuses any existing same-named form input as its
      // hidden field; with our forms that's the visible input itself, which
      // v29 would then overwrite with the full E164 number on submit. Clear
      // the visible input's name only for the duration of init so v29 creates
      // a DEDICATED hidden input, then restore the name. This mirrors the
      // pre-v29 setup (visible input displays the national number; a separate
      // hidden input carries the E164 value) and keeps the name in place for
      // form validation (which keys off [name=...]) and submission.
      const submitName = input.dataset.hiddenInput || input.getAttribute('name')
      const originalName = input.getAttribute('name')
      if (originalName) input.removeAttribute('name')
      intlTelInput(input, {
        uiTranslations: intlTelInputI18n[I18n.locale],
        placeholderNumberPolicy: 'AGGRESSIVE',
        countryOrder: countryOrder,
        initialCountry: initialCountry(),
        hiddenInputs: () => {
          return {
            phone: submitName,
            country: 'intlTelInput[country]',
          }
        },
      })
      if (originalName) input.setAttribute('name', originalName)
      followCountryForSmsConsent(input)
    }
  })
}

// The SMS opt-in rendered under a mobile field (data-sms-consent names its
// block) is only for +1 numbers: texts to the US and Canada go out from our
// toll-free number, which needs the recipient's express consent. Show the
// block while a +1 country is selected and hide it otherwise.
const followCountryForSmsConsent = (input) => {
  const block =
    input.dataset.smsConsent &&
    document.getElementById(input.dataset.smsConsent)
  if (!block) return

  const iti = window.intlTelInput.getInstance(input)
  const toggle = () => {
    block.hidden = iti.getSelectedCountry()?.dialCode !== '1'
  }
  input.addEventListener('countrychange', toggle)
  toggle()
}
;['DOMContentLoaded', 'turbolinks:load'].forEach((event) => {
  document.addEventListener(event, () => {
    initIntlTelInputs('.intl-tel-input')
  })
})
